import http, { request } from './http';

export interface IssueLifecycleRecordItem {
  id: string;
  action: string;
  from: string;
  to: string;
  operatorUserId?: number | null;
  operatorName?: string | null;
  opinion?: string | null;
  handlerUserId?: number | null;
  attachmentUrls?: string[] | null;
  handledAt?: string;
}

export interface IssueCommentItem {
  id: string;
  parentId?: string | null;
  authorUserId?: number | null;
  authorName?: string | null;
  replyToName?: string | null;
  content: string;
  createdAt?: string;
  deleted?: boolean | null;
}

export interface TaskItem {
  id: number;
  projectId: number;
  title: string;
  description?: string;
  issueAttachmentUrl?: string | null;
  issueAttachmentDeleted?: boolean | null;
  postResolutionAttachmentUrl?: string | null;
  postResolutionAttachmentDeleted?: boolean | null;
  issueSource?: string | null;
  solutionRemarks?: string | null;
  status?: string;
  appealReason?: string | null;
  /** 生命周期六态中文标签或 legacy PENDING/RESOLVED */
  issueListStatus?: string | null;
  issueNotifyOnTransition?: boolean | null;
  issueLifecycleRecords?: IssueLifecycleRecordItem[] | null;
  issueComments?: IssueCommentItem[] | null;
  assignee?: string;
  assigneeUserId?: number | null;
  assigneeUserIds?: number[] | null;
  ownerUserId?: number | null;
  issueOperationRecords?: IssueOperationRecordItem[] | null;
  creatorUserId?: number | null;
  /** 是否由「一诺」在 AI 创建项目会话中生成 */
  yinoAiCreated?: boolean | null;
  creatorDisplayName?: string | null;
  dueDate?: string;
  /** WBS 启动时间（三方 start_date） */
  startDate?: string;
  priority?: string;
  phaseId?: number;
  phaseName?: string;
  phaseCode?: string;
  autoGenerated?: boolean;
  /** 问题清单序号 */
  issueNumber?: number | null;
  /** 展示编号，如 PRJ001-001 */
  issueNumberLabel?: string | null;
  /** 问题分类 */
  issueCategory?: string | null;
  closedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  /** 流程子类型：TR_PHASE_GATE / WBS 等 */
  taskKind?: string | null;
  /** TR 审核：AI 是否建议通过 */
  trAiRecommendPass?: boolean | null;
  /** WBS 任务编码 */
  taskCode?: string | null;
  parentTaskId?: number | null;
  wbsSourceKey?: string | null;
  /** 外部 WBS 阶段 ID：concept/plan/...（兼容 APQP1–5） */
  wbsPhaseId?: string | null;
  workload?: string | null;
  /** 关联 WBS 任务 id */
  linkedWbsTaskIds?: number[] | null;
  reminderAt?: string | null;
  reminderCount?: number | null;
  warningLevel?: string | null;
  reminderSentCount?: number | null;
}

export async function getMyTasks(): Promise<TaskItem[]> {
  const res = await request<{ success: boolean; data?: TaskItem[] }>(
    http.get('/tasks/my')
  );
  return (res as { data?: TaskItem[] })?.data ?? [];
}

export interface IssueOperationRecordItem {
  handlerUserId: number;
  handledAt: string;
  suggestion: string;
  attachmentUrls?: string[] | null;
}

export interface CreateTaskPayload {
  projectId: number;
  title: string;
  description?: string;
  issueAttachmentUrl?: string;
  postResolutionAttachmentUrl?: string;
  issueSource?: string;
  solutionRemarks?: string;
  priority?: string;
  dueDate?: string;
  assignee?: string;
  assigneeUserId?: number;
  assigneeUserIds?: number[];
  /** 不传时后端默认 TODO；AI 关键问题同步为问题清单时使用 open */
  status?: string;
  /** 生命周期六态或 legacy PENDING/RESOLVED */
  issueListStatus?: string;
  /** 项目阶段实例 id，不传则后端默认当前项目阶段 */
  projectPhaseId?: number;
  issueCategory?: string;
  /** 是否向处理人发送 IM 即时通知；默认 true */
  sendInstantImNotify?: boolean;
  /** 状态流转时是否向处理人和创建人发送即时通知；不传或 true 时发送 */
  issueNotifyOnTransition?: boolean;
  linkedWbsTaskIds?: number[];
  reminderAt?: string;
  reminderCount?: number;
  warningLevel?: string;
}

export async function listProjectTasks(projectId: number): Promise<TaskItem[]> {
  const res = await request<{ success?: boolean; data?: TaskItem[] }>(http.get(`/projects/${projectId}/tasks`));
  return res?.data ?? [];
}

export interface IssueChecklistQuery {
  page?: number;
  size?: number;
  keyword?: string;
  assigneeUserIds?: number[];
  assigneeSelf?: boolean;
  assigneeBlank?: boolean;
  creatorUserIds?: number[];
  creatorSelf?: boolean;
  creatorYino?: boolean;
  severity?: string;
  status?: string;
  category?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  sort?: string;
}

export interface PagedTaskResult {
  records: TaskItem[];
  total: number;
  page: number;
  size: number;
}

/** Spring `@RequestParam List` 需要 `a=1&a=2`，axios 默认会编成 `a[]=1` 导致绑不上 */
function serializeIssueChecklistParams(query: IssueChecklistQuery): URLSearchParams {
  const sp = new URLSearchParams();
  const append = (key: string, value: string | number | boolean | undefined | null) => {
    if (value === undefined || value === null || value === '') return;
    sp.append(key, String(value));
  };
  append('page', query.page);
  append('size', query.size);
  append('keyword', query.keyword);
  append('assigneeSelf', query.assigneeSelf);
  append('assigneeBlank', query.assigneeBlank);
  append('creatorSelf', query.creatorSelf);
  append('creatorYino', query.creatorYino);
  append('severity', query.severity);
  append('status', query.status);
  append('category', query.category);
  append('dueDateFrom', query.dueDateFrom);
  append('dueDateTo', query.dueDateTo);
  append('sort', query.sort);
  for (const id of query.assigneeUserIds ?? []) {
    if (id != null && Number(id) > 0) sp.append('assigneeUserIds', String(id));
  }
  for (const id of query.creatorUserIds ?? []) {
    if (id != null && Number(id) > 0) sp.append('creatorUserIds', String(id));
  }
  return sp;
}

export async function listProjectIssueChecklist(
  projectId: number,
  query: IssueChecklistQuery = {},
): Promise<PagedTaskResult> {
  const res = await request<{ success?: boolean; data?: PagedTaskResult }>(
    http.get(`/projects/${projectId}/tasks/issues`, {
      params: serializeIssueChecklistParams(query),
    }),
  );
  const data = res?.data;
  return {
    records: data?.records ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? query.page ?? 1,
    size: data?.size ?? query.size ?? 10,
  };
}

export async function listProjectWorkflowOpenTasks(projectId: number): Promise<TaskItem[]> {
  const res = await request<{ success?: boolean; data?: TaskItem[] }>(
    http.get(`/projects/${projectId}/tasks/workflow-open`),
  );
  return res?.data ?? [];
}

export async function getProjectMyIssueStats(
  projectId: number,
): Promise<{ myOpen: number; myClosed: number }> {
  const res = await request<{ success?: boolean; data?: { myOpen: number; myClosed: number } }>(
    http.get(`/projects/${projectId}/tasks/my-issue-stats`),
  );
  return res?.data ?? { myOpen: 0, myClosed: 0 };
}

export async function getTaskById(taskId: number): Promise<TaskItem> {
  const res = await request<{ success?: boolean; data?: TaskItem }>(http.get(`/tasks/${taskId}`));
  const data = res?.data;
  if (!data) throw new Error('任务不存在');
  return data;
}

export async function listProjectIssueCategories(projectId: number): Promise<string[]> {
  const res = await request<{ success?: boolean; data?: string[] }>(
    http.get(`/projects/${projectId}/tasks/issue-categories`),
  );
  return res?.data ?? [];
}

export async function createTask(payload: CreateTaskPayload): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post('/tasks', payload)
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('创建失败');
  return data;
}

export async function createProjectMemberTodo(payload: CreateTaskPayload): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post('/tasks/project-member-todo', payload)
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('创建失败');
  return data;
}

export async function deleteTask(id: number): Promise<void> {
  await request<void>(http.delete(`/tasks/${id}`));
}

export async function closeIssueTask(id: number): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.patch(`/tasks/${id}/issue-close`),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('关闭失败');
  return data;
}

export async function appendIssueSolution(taskId: number, solutionRemarks: string): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.patch(`/tasks/${taskId}/issue-solution`, { solutionRemarks }),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('提交失败');
  return data;
}

export interface UpdateIssuePayload {
  title?: string;
  description?: string;
  assigneeUserIds: number[];
  priority?: string;
  issueCategory?: string;
  issueListStatus?: string;
  issueAttachmentUrl?: string;
  dueDate?: string;
  linkedWbsTaskIds?: number[];
  reminderAt?: string;
  reminderCount?: number;
  warningLevel?: string;
  clearReminder?: boolean;
  projectPhaseId?: number;
  /** 状态流转时是否向处理人和创建人发送即时通知；null=保持不变 */
  issueNotifyOnTransition?: boolean;
}

export async function updateIssueTask(taskId: number, payload: UpdateIssuePayload): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.patch(`/tasks/${taskId}/issue`, payload),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('保存失败');
  return data;
}

export async function urgeIssueTask(taskId: number): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post(`/tasks/${taskId}/issue-urge`),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('催办失败');
  return data;
}

export interface AppendIssueOperationPayload {
  suggestion: string;
  attachmentUrls?: string[];
}

export async function appendIssueOperation(
  taskId: number,
  payload: AppendIssueOperationPayload,
): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.patch(`/tasks/${taskId}/issue-operation`, payload),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('提交失败');
  return data;
}

export interface TransitionIssueLifecyclePayload {
  targetStatus: string;
  opinion?: string;
  handlerUserIds?: number[];
  attachmentUrls?: string[];
}

export async function transitionIssueLifecycle(
  taskId: number,
  payload: TransitionIssueLifecyclePayload,
): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post(`/tasks/${taskId}/issue-lifecycle`, payload),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('状态更新失败');
  return data;
}

export interface AddIssueCommentPayload {
  content: string;
  parentId?: string;
  mentionedUserIds?: number[];
}

export async function addIssueComment(
  taskId: number,
  payload: AddIssueCommentPayload,
): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post(`/tasks/${taskId}/issue-comments`, payload),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('评论发布失败');
  return data;
}

/** WBS 任务评论（支持 @ 成员并推送飞书通知） */
export async function addWbsComment(
  taskId: number,
  payload: AddIssueCommentPayload,
): Promise<TaskItem> {
  const res = await request<{ success: boolean; data?: TaskItem }>(
    http.post(`/tasks/${taskId}/wbs-comments`, payload),
  );
  const data = (res as { data?: TaskItem })?.data;
  if (!data) throw new Error('评论发布失败');
  return data;
}

export type WbsTaskStatus =
  | 'PENDING_CONFIRM'
  | 'ACCEPTED'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'REJECTED'
  | 'APPEAL';

export interface ListWbsTasksQuery {
  mineOnly?: boolean;
  keyword?: string;
  status?: string;
}

export async function listProjectWbsTasks(
  projectId: number,
  query: ListWbsTasksQuery = {},
): Promise<TaskItem[]> {
  const res = await request<{ success?: boolean; data?: TaskItem[] }>(
    http.get(`/projects/${projectId}/tasks/wbs`, { params: query }),
  );
  return res?.data ?? [];
}

export interface CreateWbsTaskPayload {
  title: string;
  description?: string;
  parentTaskId?: number | null;
  ownerUserId?: number | null;
  assigneeUserId?: number | null;
  assigneeUserIds?: number[];
  priority?: string;
  startDate?: string;
  dueDate?: string;
  workload?: string;
  /** 外部 WBS 阶段 ID：concept/plan/development/validation/release */
  wbsPhaseId?: string;
  status?: string;
}

export async function createWbsTask(projectId: number, payload: CreateWbsTaskPayload): Promise<TaskItem> {
  const res = await request<{ success?: boolean; data?: TaskItem }>(
    http.post(`/projects/${projectId}/tasks/wbs`, { ...payload, projectId }),
  );
  const data = res?.data;
  if (!data) throw new Error('创建失败');
  return data;
}

export interface UpdateWbsTaskPayload {
  title?: string;
  description?: string;
  parentTaskId?: number | null;
  assigneeUserId?: number | null;
  assigneeUserIds?: number[];
  ownerUserId?: number | null;
  priority?: string;
  startDate?: string;
  dueDate?: string;
  workload?: string;
  status?: string;
  appealReason?: string | null;
  /** 启动阶段：concept/plan/development/validation/release 或 CONCEPT/PLAN/... */
  wbsPhaseId?: string | null;
  issueAttachmentUrl?: string | null;
}

export async function updateWbsTask(taskId: number, payload: UpdateWbsTaskPayload): Promise<TaskItem> {
  const res = await request<{ success?: boolean; data?: TaskItem }>(
    http.patch(`/tasks/${taskId}/wbs`, payload),
  );
  const data = res?.data;
  if (!data) throw new Error('保存失败');
  return data;
}

export async function deleteWbsTask(taskId: number): Promise<void> {
  await request<void>(http.delete(`/tasks/${taskId}/wbs`));
}
